import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';


const Beats = ({ setWonAmount, flagToTriggerHandleHit, isGameActive, toggleGameActive }) => {
  // Create references
  const outerContainerRef = useRef(null);
  const scrollContentRef = useRef(null);
  const [initialItems, setInitialItems] = useState([]);
  const [currentIndexShown, setCurrentIndexShown] = useState(0);
  const [hitAtValid, setHitAtValid] = useState(''); // State for feedback message
  const currentIndexRef = useRef(0); // Ref to track the current index
  const itemsInSquareRef = useRef([]); // Ref to track items currently inside the red square
  const correctHitsRef = useRef(new Set()); // Track correctly hit items by their indices
  const failedChunksRef = useRef(new Set()); // Track chunks that had failed attempts
  const wonChunksRef = useRef(new Set()); // Track chunks where all valid items were hit
  const passedValidItemsRef = useRef(new Set()); // Track valid items that passed without being hit
  const pendingChunkUpdates = useRef(null); // Track pending chunk updates
  const isGameActiveRef = useRef(isGameActive);
  const animationRef = useRef(null);
  const gapSize = 0;
  const gameStartTimestampRef = useRef(null);
  const GRACE_PERIOD_MS = 2000; // 2 seconds grace period after game starts

  // Configuration
  const squareSize = 30;
  const buffer = 4; // Extra items to render on each side for smooth scrolling
  const [scrollSpeed, setScrollSpeed] = useState(3); // Keep this for initial value
  const scrollSpeedRef = useRef(3); // Add this new ref for actual animation use
  const validSpacePosition = 0.5; // As a percentage (0.5 = 50%, 0.3 = 30%, etc.)
  const [startFrom, setStartFrom] = useState(() => {
    // Calculate initial value based on current window aspect ratio
    const aspectRatio = window.innerWidth / window.innerHeight;
    return aspectRatio >= 1 ? 32 : 16; // 32 for landscape/square, 16 for portrait
  });

  // Define margin values
  const fourthItemMargin = squareSize;
  const eighthItemMargin = squareSize * 2;

  function getRandomInt(max) {
    return Math.floor(Math.random() * max);
  }

  // Function to generate lines of text with random 0s and 1s
  const generateBars = useCallback((lineCount, length = 8) => {
    const lines = [];
    for (let i = 0; i < lineCount; i++) {
      // Start with all false values
      const lineArray = Array(length).fill(false);

      if (isGameActive) {
        // Weighted random selection for the number of true values (2, 3, or 4)
        // Weights: 20% chance for 2, 50% chance for 3, 30% chance for 4
        const random = Math.random();
        let numTrueValues;

        if (random < 0.2) {
          numTrueValues = 2; // 20% chance - least common
        } else if (random < 0.7) {
          numTrueValues = 3; // 50% chance - most common
        } else {
          numTrueValues = 4; // 30% chance - medium common
        }

        // Create an array of possible indices (0 to length-1)
        const possibleIndices = Array.from({ length }, (_, i) => i);

        // Randomly select numTrueValues indices and set them to true
        for (let j = 0; j < numTrueValues; j++) {
          if (possibleIndices.length === 0) break;

          // Get a random index from possibleIndices
          const randomIndexPos = getRandomInt(possibleIndices.length);
          const indexToSetTrue = possibleIndices[randomIndexPos];

          // Set the value to true
          lineArray[indexToSetTrue] = true;

          // Remove the used index from possibleIndices
          possibleIndices.splice(randomIndexPos, 1);
        }
      }

      // Push the same line into the lines array
      lines.push(lineArray, lineArray, lineArray, lineArray);
    }
    return lines.flat(); // Flatten to a single array
  }, [isGameActive]);

  // Generate a large dataset - regenerate when isGameActive changes
  const totalItems = 4000; // Virtual length of our infinite scroller
  const [allItems, setAllItems] = useState(() => generateBars(totalItems / 8));

  // Regenerate items when game active state changes - this is important!
  useEffect(() => {
    console.log("Game state changed:", isGameActive);
    setAllItems(generateBars(totalItems / 8));
  }, [isGameActive, generateBars]);

  // Update the ref whenever isGameActive changes
  useEffect(() => {
    isGameActiveRef.current = isGameActive;
    console.log("Updated isGameActiveRef to:", isGameActive);
  }, [isGameActive]);

  // Helper function to get width of an item including its margin
  const getItemFullWidth = useCallback((index) => {
    const value = index + 1;
    if (value % 8 === 0) return squareSize + eighthItemMargin;
    if (value % 4 === 0) return squareSize + fourthItemMargin;
    return squareSize + gapSize; // Add 5px gap between regular items
  }, [squareSize, fourthItemMargin, eighthItemMargin]);

  // Pre-calculate item positions
  const getItemsWithPositions = useCallback((startIndex, count) => {
    const items = [];
    let position = 0;

    for (let i = 0; i < count; i++) {
      const index = (startIndex + i) % totalItems;
      const value = index + 1;
      const isEight = value % 8 === 0;
      const isFour = value % 4 === 0 && !isEight;
      const margin = isEight ? eighthItemMargin : (isFour ? fourthItemMargin : gapSize); // Add 5px gap for regular items

      items.push({
        index,
        value: allItems[index],
        displayValue: value,
        position,
        width: squareSize,
        margin,
        isEight,
        isFour
      });

      position += squareSize + margin;
    }

    return items;
  }, [allItems, totalItems, squareSize, fourthItemMargin, eighthItemMargin]);

  // Helper function to reset game state
  const resetGameState = useCallback(() => {
    // Clear all references and states
    correctHitsRef.current.clear();
    failedChunksRef.current.clear();
    wonChunksRef.current.clear();
    passedValidItemsRef.current.clear();
    setHitAtValid('');
    setWonAmount(0);

    // Set the game start timestamp when resetting
    gameStartTimestampRef.current = Date.now();

    // Remove all failed-attempt and won-chunk classes from DOM
    if (scrollContentRef.current) {
      const chunkContainers = scrollContentRef.current.querySelectorAll('.chunk-container');
      chunkContainers.forEach(container => {
        // Remove all classes during reset
        container.classList.remove('failed-attempt');
        container.classList.remove('won-chunk');
      });
    }
  }, [setWonAmount]);

  // Reset when game state changes
  useEffect(() => {
    if (isGameActive) {
      // Set the game start timestamp when the game becomes active
      gameStartTimestampRef.current = Date.now();
      // Reset game state
      resetGameState();
    } else {
      // Clear the timestamp when the game is not active
      gameStartTimestampRef.current = null;
    }
  }, [isGameActive, resetGameState]);

  // Define the functions before they are used
  const updateChunkClassList = useCallback((chunkId, action, className) => {
    if (!pendingChunkUpdates.current) {
      pendingChunkUpdates.current = [];
      requestAnimationFrame(() => {
        const scrollContent = scrollContentRef.current;
        if (scrollContent) {
          const chunkContainers = scrollContent.querySelectorAll('.chunk-container');
          pendingChunkUpdates.current.forEach(update => {
            chunkContainers.forEach(container => {
              if (parseInt(container.dataset.chunkId, 10) === update.chunkId) {
                container.classList[update.action](update.className);
              }
            });
          });
          pendingChunkUpdates.current = null;
        }
      });
    }

    pendingChunkUpdates.current.push({ chunkId, action, className });
  }, []);

  const checkChunkCompletion = useCallback((chunkId) => {
    const startIdx = chunkId;
    const endIdx = startIdx + 7;

    let allValid = true;
    let hasValidItems = false;

    for (let i = startIdx; i <= endIdx; i++) {
      const idx = i % totalItems;
      if (allItems[idx] === true) {
        hasValidItems = true;
        if (!correctHitsRef.current.has(idx)) {
          allValid = false;
          break;
        }
      }
    }

    return hasValidItems && allValid;
  }, [allItems, totalItems]);

  // Modify the updateItemsInSquare function to use the grace period
  const updateItemsInSquare = (itemsData, scrollOffset) => {
    const container = outerContainerRef.current;
    const squareLeft = container.clientWidth * validSpacePosition;
    const squareRight = squareLeft + squareSize;

    // CHANGE: Significantly increase the grace window (from 0.75 to 2.0 squares)
    const graceWindowBefore = squareLeft - (squareSize * 2.0);
    const graceWindowAfter = squareRight + (squareSize * 2.0);

    const itemsInSquare = [];
    const approachingItems = [];
    const recentlyPassedItems = [];

    // Track items that just passed the valid space without being hit
    let chunksToMark = new Set();

    for (let i = 0; i < itemsData.length; i++) {
      const item = itemsData[i];
      const itemPosition = item.position - scrollOffset;
      const itemRight = itemPosition + squareSize;

      // Items in the valid-space
      if (itemPosition < squareRight && itemRight > squareLeft) {
        itemsInSquare.push({
          index: item.index,
          value: isGameActiveRef.current ? allItems[item.index] : false,
          position: itemPosition,
          distance: 0
        });
      }
      // Items approaching the valid-space (within grace window)
      else if (itemPosition >= squareRight && itemPosition < graceWindowAfter) {
        if (isGameActiveRef.current && allItems[item.index]) {
          approachingItems.push({
            index: item.index,
            value: true,
            position: itemPosition,
            distance: itemPosition - squareRight
          });
        }
      }
      // Items that just passed the valid-space (within grace window)
      else if (itemRight > graceWindowBefore && itemRight <= squareLeft) {
        if (isGameActiveRef.current && allItems[item.index]) {
          recentlyPassedItems.push({
            index: item.index,
            value: true,
            position: itemPosition,
            distance: squareLeft - itemRight
          });
        }
      }

      // CRITICAL: Check for valid items that have just passed the valid-space without being hit
      if (isGameActiveRef.current &&
          allItems[item.index] === true &&
          itemRight <= squareLeft &&
          // CHANGE: Increase this distance too (from 1.0 to 2.5 squares)
          itemRight > squareLeft - (squareSize * 2.5) &&
          !correctHitsRef.current.has(item.index) &&
          !passedValidItemsRef.current.has(item.index)) {

        // Mark this item as passed
        passedValidItemsRef.current.add(item.index);

        // Get the chunk ID and add to our set of chunks to mark
        const chunkId = Math.floor(item.index / 8) * 8;
        chunksToMark.add(chunkId);
      }
    }

    // First prioritize items in the square
    if (itemsInSquare.length > 0) {
      itemsInSquareRef.current = itemsInSquare.sort((a, b) => a.position - b.position);
    }
    // Then check approaching items (closer items first)
    else if (approachingItems.length > 0) {
      itemsInSquareRef.current = approachingItems.sort((a, b) => a.distance - b.distance);
    }
    // Finally check recently passed items (closer items first)
    else if (recentlyPassedItems.length > 0) {
      itemsInSquareRef.current = recentlyPassedItems.sort((a, b) => a.distance - b.distance);
    }
    else {
      itemsInSquareRef.current = [];
    }

    // IMPORTANT FIX: Only mark chunks as failed if:
    // 1. Gameplay has actually started
    // 2. We're past the initial grace period
    const isInGracePeriod = gameStartTimestampRef.current &&
                           (Date.now() - gameStartTimestampRef.current < GRACE_PERIOD_MS);

    if (isGameActiveRef.current && !isInGracePeriod) {
      // Mark chunks with passed valid items as failed
      chunksToMark.forEach(chunkId => {
        // Only mark as failed if not already won AND not a starter chunk
        if (!wonChunksRef.current.has(chunkId)) {
          failedChunksRef.current.add(chunkId);

          // Find and update the DOM
          const chunkContainers = scrollContentRef.current.querySelectorAll('.chunk-container');
          chunkContainers.forEach(container => {
            if (parseInt(container.dataset.chunkId, 10) === chunkId &&
                container.dataset.starterChunk !== "true") {
              container.classList.add('failed-attempt');
            }
          });
        }
      });
    }
  };

  // Update the handleHit function to better handle multiple hits in succession
  const handleHit = useCallback(() => {
    // Only process hits if game is active
    if (!isGameActive) {
      setHitAtValid('inactive');
      return;
    }


    const itemsInSquare = itemsInSquareRef.current;

    // If there are items in our expanded detection area
    if (itemsInSquare && itemsInSquare.length > 0) {
      // First, get all valid items that haven't been hit yet
      const validUnhitItems = itemsInSquare.filter(item =>
        item.value === true && !correctHitsRef.current.has(item.index)
      );

      if (validUnhitItems.length > 0) {
        // We have valid items that haven't been hit yet - take the closest one
        // Sort by absolute distance to handle both approaching and passed items
        const closestValidItem = validUnhitItems.sort((a, b) =>
          Math.abs(a.distance || 0) - Math.abs(b.distance || 0)
        )[0];

        // Mark this item as hit
        markItemAsHit(closestValidItem);
      } else {
        // All valid items in range have already been hit
        // Check if there are any valid items at all
        const anyValidItem = itemsInSquare.find(item => item.value === true);

        if (anyValidItem) {
          // There are valid items but they've all been hit
          // Still give positive feedback to make the game feel responsive
          setHitAtValid('good');
          setTimeout(() => setHitAtValid(''), 200);
        } else {
          // No valid items found in our grace window
          handleMissedHit();
        }
      }
    } else {
      // No items in our expanded detection area
      handleMissedHit();
    }

    // Highlight the valid space
    const validSpace = document.querySelector('.valid-space');
    if (validSpace) {
      validSpace.classList.add('valid-highlight');
      setTimeout(() => {
        validSpace.classList.remove('valid-highlight');
      }, 100);
    }
  }, [isGameActive, setWonAmount, checkChunkCompletion, updateChunkClassList, validSpacePosition, squareSize]);

  // Helper function to mark an item as hit and handle success logic
  const markItemAsHit = (item) => {
    setHitAtValid('good');
    correctHitsRef.current.add(item.index);

    // Find and update the target item in the DOM
    const scrollContent = scrollContentRef.current;
    if (scrollContent) {
      const targetItem = scrollContent.querySelector(`.scroll-item[data-index="${item.index}"]`);
      if (targetItem) {
        targetItem.classList.add('correct-hit');
      }
    }

    // Use requestAnimationFrame for chunk completion logic
    requestAnimationFrame(() => {
      const chunkId = Math.floor(item.index / 8) * 8;

      if (checkChunkCompletion(chunkId)) {
        failedChunksRef.current.delete(chunkId);
        wonChunksRef.current.add(chunkId);
        setWonAmount(wonChunksRef.current.size);
        updateChunkClassList(chunkId, 'remove', 'failed-attempt');
        updateChunkClassList(chunkId, 'add', 'won-chunk');
      }
    });

    // Reset state after a short delay
    requestAnimationFrame(() => {
      setHitAtValid('');
    });
  };

  // Helper function to handle missed hit logic
  const handleMissedHit = () => {
    setHitAtValid('bad');

    // Check if there are any valid items coming soon within a larger window
    // This makes it feel more forgiving
    const scrollContent = scrollContentRef.current;
    const container = outerContainerRef.current;

    // Get the current time for grace period check
    const currentTime = Date.now();
    const isInGracePeriod = gameStartTimestampRef.current &&
                           (currentTime - gameStartTimestampRef.current < GRACE_PERIOD_MS);

    // Don't mark chunks as failed during grace period
    if (isInGracePeriod) return;

    if (scrollContent && container) {
      // Get the position of the valid-space
      const squareLeft = container.clientWidth * validSpacePosition;
      const squareRight = squareLeft + squareSize;

      // Get the scroll offset as a number
      const transformStyle = scrollContent.style.transform;
      let scrollOffsetPx = 0;
      if (transformStyle) {
        const match = transformStyle.match(/translateX\(-(\d+(\.\d+)?)px\)/);
        if (match && match[1]) {
          scrollOffsetPx = parseFloat(match[1]);
        }
      }

      // Look for the closest chunk container to mark as failed
      const chunkContainers = scrollContent.querySelectorAll('.chunk-container');
      chunkContainers.forEach(container => {
        const containerLeft = parseInt(container.style.left, 10) - scrollOffsetPx;
        const containerWidth = container.offsetWidth;
        const containerRight = containerLeft + containerWidth;

        // Check if container is in or very close to the valid-space
        if ((containerLeft < squareRight && containerRight > squareLeft)) {
          const chunkId = parseInt(container.dataset.chunkId, 10);

          // Check if this chunk already has successful hits
          const hasSuccessfulHits = Array.from(container.querySelectorAll('.scroll-item'))
            .some(item => item.classList.contains('correct-hit'));

          // Only mark as failed if no successful hits yet AND not a starter chunk
          if (!hasSuccessfulHits && chunkId !== undefined && container.dataset.starterChunk !== "true") {
            container.classList.add('failed-attempt');
            failedChunksRef.current.add(chunkId);

            // If a chunk fails, it can't be a won chunk
            if (wonChunksRef.current.has(chunkId)) {
              wonChunksRef.current.delete(chunkId);
              container.classList.remove('won-chunk');
            }
          }
        }
      });
    }
  };

  // Update to trigger handleHit on flagToTriggerHandleHit changes
  useEffect(() => {
    if (flagToTriggerHandleHit !== undefined) {
      console.log('Flag triggered handleHit');
      handleHit();
    }
  }, [flagToTriggerHandleHit, handleHit]);

  // Add event listener for keydown with the updated handler
  useEffect(() => {
    const keyHandler = (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        handleHit();
      }
    };

    const danceEventHandler = () => {
      console.log('Dance button clicked, game active:', isGameActive);
      handleHit();
    };

    // Add event listeners
    window.addEventListener('keydown', keyHandler);
    window.addEventListener('dance-triggered', danceEventHandler);
    console.log('Added event listeners');

    // Clean up
    return () => {
      console.log('Removed event listeners');
      window.removeEventListener('keydown', keyHandler);
      window.removeEventListener('dance-triggered', danceEventHandler);
    };
  }, [handleHit]);

  // Reset hitAtValid when currentIndexShown changes
  useEffect(() => {
    setHitAtValid(''); // Clear message when currentIndexShown changes
  }, [currentIndexShown]);

  // Main animation and scrolling effect
  useEffect(() => {
    const container = outerContainerRef.current;
    const scrollContent = scrollContentRef.current;
    if (!container || !scrollContent) return;

    // Calculate initial visible count based on container width
    const getVisibleCount = () => {
      const containerWidth = container.clientWidth;
      let count = 0;
      let width = 0;

      // Keep adding items until we fill the container width plus buffer
      while (width < containerWidth + (2 * squareSize * buffer)) {
        width += getItemFullWidth(count);
        count++;
      }

      return count + buffer; // Add extra buffer for smooth scrolling
    };

    // Set initial items
    const initialCount = getVisibleCount();
    const initialItemsData = getItemsWithPositions(0, initialCount);
    setInitialItems(initialItemsData);

    // Animation state variables
    let currentIndex = 0;
    currentIndexRef.current = 0; // Initialize the ref
    setCurrentIndexShown(0); // New variable to track the displayed index
    let scrollOffset = 0;
    let itemsData = initialItemsData;
    let hasSetNull = false;

    // Helper function to get chunk ID based on first item index
    const getChunkId = (firstItemIndex) => {
      // A chunk ID is based on the index of the first item in the chunk
      // This creates a stable ID even as chunks are recreated
      return Math.floor(firstItemIndex / 8) * 8;
    };

    // Function to update items in red square
    const updateItemsInSquare = (itemsData, scrollOffset) => {
      const squareLeft = container.clientWidth * validSpacePosition;
      const squareRight = squareLeft + squareSize;

      // Add a grace period window on both sides of the valid-space
      const graceWindowBefore = squareLeft - (squareSize * 2.0);
      const graceWindowAfter = squareRight + (squareSize * 2.0);

      const itemsInSquare = [];
      const approachingItems = [];
      const recentlyPassedItems = [];

      // Track items that just passed the valid space without being hit
      let chunksToMark = new Set();

      for (let i = 0; i < itemsData.length; i++) {
        const item = itemsData[i];
        const itemPosition = item.position - scrollOffset;
        const itemRight = itemPosition + squareSize;

        // Items in the valid-space
        if (itemPosition < squareRight && itemRight > squareLeft) {
          itemsInSquare.push({
            index: item.index,
            value: isGameActiveRef.current ? allItems[item.index] : false,
            position: itemPosition,
            distance: 0
          });
        }
        // Items approaching the valid-space (within grace window)
        else if (itemPosition >= squareRight && itemPosition < graceWindowAfter) {
          if (isGameActiveRef.current && allItems[item.index]) {
            approachingItems.push({
              index: item.index,
              value: true,
              position: itemPosition,
              distance: itemPosition - squareRight
            });
          }
        }
        // Items that just passed the valid-space (within grace window)
        else if (itemRight > graceWindowBefore && itemRight <= squareLeft) {
          if (isGameActiveRef.current && allItems[item.index]) {
            recentlyPassedItems.push({
              index: item.index,
              value: true,
              position: itemPosition,
              distance: squareLeft - itemRight
            });
          }
        }

        // CRITICAL: Check for valid items that have just passed the valid-space without being hit
        if (isGameActiveRef.current &&
            allItems[item.index] === true &&
            itemRight <= squareLeft &&
            // CHANGE: Increase this distance too (from 1.0 to 2.5 squares)
            itemRight > squareLeft - (squareSize * 2.5) &&
            !correctHitsRef.current.has(item.index) &&
            !passedValidItemsRef.current.has(item.index)) {

          // Mark this item as passed
          passedValidItemsRef.current.add(item.index);

          // Get the chunk ID and add to our set of chunks to mark
          const chunkId = Math.floor(item.index / 8) * 8;
          chunksToMark.add(chunkId);
        }
      }

      // First prioritize items in the square
      if (itemsInSquare.length > 0) {
        itemsInSquareRef.current = itemsInSquare.sort((a, b) => a.position - b.position);
      }
      // Then check approaching items (closer items first)
      else if (approachingItems.length > 0) {
        itemsInSquareRef.current = approachingItems.sort((a, b) => a.distance - b.distance);
      }
      // Finally check recently passed items (closer items first)
      else if (recentlyPassedItems.length > 0) {
        itemsInSquareRef.current = recentlyPassedItems.sort((a, b) => a.distance - b.distance);
      }
      else {
        itemsInSquareRef.current = [];
      }

      // IMPORTANT FIX: Only mark chunks as failed if:
      // 1. Gameplay has actually started
      // 2. We're past the initial grace period
      const isInGracePeriod = gameStartTimestampRef.current &&
                             (Date.now() - gameStartTimestampRef.current < GRACE_PERIOD_MS);

      if (isGameActiveRef.current && !isInGracePeriod) {
        // Mark chunks with passed valid items as failed
        chunksToMark.forEach(chunkId => {
          // Only mark as failed if not already won AND not a starter chunk
          if (!wonChunksRef.current.has(chunkId)) {
            failedChunksRef.current.add(chunkId);

            // Find and update the DOM
            const chunkContainers = scrollContent.querySelectorAll('.chunk-container');
            chunkContainers.forEach(container => {
              if (parseInt(container.dataset.chunkId, 10) === chunkId &&
                  container.dataset.starterChunk !== "true") {
                container.classList.add('failed-attempt');
              }
            });
          }
        });
      }
    };

    // Directly modify DOM for better performance
    const updateItemsDOM = (itemsData, currentIndex) => {
      // Clear existing content
      while (scrollContent.firstChild) {
        scrollContent.removeChild(scrollContent.firstChild);
      }

      // Create a DocumentFragment to batch updates
      const fragment = document.createDocumentFragment();

      // Group items into chunks of 8 (natural grouping based on your data pattern)
      const chunks = [];
      let currentChunk = [];

      itemsData.forEach((item, index) => {
        currentChunk.push(item);

        // After adding an 8th item or reaching the end, create a new chunk
        if ((item.index + 1) % 8 === 0 || index === itemsData.length - 1) {
          if (currentChunk.length > 0) {
            chunks.push([...currentChunk]); // Create a copy of the current chunk
            currentChunk = []; // Clear the current chunk
          }
        }
      });

      // Process each chunk
      chunks.forEach(chunk => {
        if (chunk.length === 0) return;

        // Create a container for the chunk
        const chunkContainer = document.createElement('div');
        chunkContainer.className = 'chunk-container';

        // Get the chunk ID based on the first item's index
        const chunkId = getChunkId(chunk[0].index);

        // FIRST - check if this is a starter chunk (BEFORE applying any other classes)
        const isStarterChunk = isGameActiveRef.current && currentIndex < startFrom && chunkId < startFrom;
        if (isStarterChunk) {
          chunkContainer.dataset.starterChunk = "true";

          // Force all items to be inactive
          chunk.forEach(item => {
            item.value = false;
          });

          // Make sure it's not in the failed chunks set
          failedChunksRef.current.delete(chunkId);
        }

        // Get the current time for grace period check
        const currentTime = Date.now();
        const isInGracePeriod = gameStartTimestampRef.current &&
                               (currentTime - gameStartTimestampRef.current < GRACE_PERIOD_MS);

        // THEN - only apply failed-attempt class if:
        // 1. It's NOT a starter chunk
        // 2. Gameplay has actually started
        // 3. It's in the failedChunksRef set
        // 4. We're not in the grace period
        if (!isStarterChunk &&
            isGameActiveRef.current &&
            !isInGracePeriod &&
            failedChunksRef.current.has(chunkId)) {
          chunkContainer.classList.add('failed-attempt');
        }

        // Check if this chunk is won and add the class if needed
        if (wonChunksRef.current.has(chunkId)) {
          chunkContainer.classList.add('won-chunk');
        }

        chunkContainer.dataset.chunkId = chunkId; // Store chunk ID for reference
        chunkContainer.style.position = 'absolute';
        chunkContainer.style.left = `${chunk[0].position}px`;
        chunkContainer.style.display = 'flex';
        chunkContainer.style.height = `${squareSize}px`;
        chunkContainer.style.gap = `${gapSize}px`; // Added gap between items using flexbox gap

        const addChunkLabel = false;

        if (addChunkLabel) {
          // ADD THIS NEW CODE: Create a chunk label on top of the chunk
          const chunkLabel = document.createElement('div');
          chunkLabel.className = 'chunk-label';
          chunkLabel.textContent = `${chunkId}`;
          chunkLabel.style.position = 'absolute';
          chunkLabel.style.top = '-20px'; // Position above the chunk
          chunkLabel.style.left = '0';
          chunkLabel.style.fontSize = '12px';
          chunkLabel.style.fontWeight = 'bold';
          chunkLabel.style.color = '#fff';
          chunkLabel.style.textShadow = '1px 1px 2px rgba(0,0,0,0.8)';
          chunkLabel.style.pointerEvents = 'none'; // Ensure it doesn't interfere with interactions

          // Add different color for starter chunks
          if (isStarterChunk) {
            chunkLabel.style.color = '#00ff00'; // Green for starter chunks
            chunkLabel.textContent = `S-${chunkId}`; // Prefix with S for starter
          }

          // Add the label to the chunk container
          chunkContainer.appendChild(chunkLabel);
        }

        // Add items to the chunk container
        chunk.forEach((item, i) => {
          // Create the item div
          const div = document.createElement('div');
          div.className = `scroll-item ${item.value ? 'active' : ''}`;
          div.style.width = `${item.width}px`;
          div.style.height = `${squareSize}px`;
          div.textContent = item.displayValue;

          // Check if this item was correctly hit
          if (correctHitsRef.current.has(item.index)) {
            div.classList.add('correct-hit');
          }

          // Create a container that includes the item and potentially a separator
          const itemContainer = document.createElement('div');
          itemContainer.style.display = 'flex';
          itemContainer.style.position = 'relative';
          itemContainer.appendChild(div);

          // Add separators for 4th and 8th items
          if ((item.index + 1) % 4 === 0 || (item.index + 1) % 8 === 0) {
            const isEight = (item.index + 1) % 8 === 0;
            const width = isEight ? eighthItemMargin : fourthItemMargin;

            // Create separator
            const separator = document.createElement('div');
            separator.className = `separator ${isEight ? 'fullbar' : ''}`;
            separator.style.width = `${width}px`;

            // Add separator to the item container
            itemContainer.appendChild(separator);
          }

          // Add the item container to the chunk container
          chunkContainer.appendChild(itemContainer);
        });

        // Add the chunk container to the fragment
        fragment.appendChild(chunkContainer);
      });

      // Append the fragment to the DOM
      scrollContent.appendChild(fragment);
    };

    // Initial DOM setup
    updateItemsDOM(itemsData, currentIndex);

    // Smooth animation loop
    const animate = () => {
      // Increment scroll offset using the ref value
      scrollOffset += scrollSpeedRef.current;

      // Get full width of current first item
      const currentItemWidth = getItemFullWidth(currentIndex % totalItems);

      // Update items in the red square
      updateItemsInSquare(itemsData, scrollOffset);

      // Get the current time for grace period check
      const currentTime = Date.now();
      const isInGracePeriod = gameStartTimestampRef.current &&
                            (currentTime - gameStartTimestampRef.current < GRACE_PERIOD_MS);

      // If game is not active OR we're in the grace period, ensure no chunks are marked as failed
      if (!isGameActiveRef.current || isInGracePeriod) {
        clearAllFailedAttemptClasses();
      }

      // EXTRA PROTECTION: Ensure no starter chunks have the failed-attempt class
      const chunkContainers = scrollContent.querySelectorAll('.chunk-container');
      chunkContainers.forEach(container => {
        if (container.dataset.starterChunk === "true" && container.classList.contains('failed-attempt')) {
          container.classList.remove('failed-attempt');

          // Also remove from the ref set
          const chunkId = parseInt(container.dataset.chunkId, 10);
          if (chunkId !== undefined) {
            failedChunksRef.current.delete(chunkId);
          }
        }
      });

      // Check if we need to scroll to next item
      if (scrollOffset >= currentItemWidth) {
        // Move to next item
        currentIndex = (currentIndex + 1) % totalItems;
        currentIndexRef.current = currentIndex; // Update the ref
        setCurrentIndexShown(currentIndex);
        hasSetNull = false;
        scrollOffset = 0;

        // Update items data with new positions
        itemsData = getItemsWithPositions(currentIndex, itemsData.length);

        // Update DOM
        updateItemsDOM(itemsData, currentIndex);

        // NEW: Check for newly completed chunks
        const visibleChunks = new Set();
        itemsData.forEach(item => {
          const chunkId = Math.floor(item.index / 8) * 8;
          visibleChunks.add(chunkId);
        });

        // Check each visible chunk to see if it's complete
        visibleChunks.forEach(chunkId => {
          if (!wonChunksRef.current.has(chunkId) && !failedChunksRef.current.has(chunkId)) {
            if (checkChunkCompletion(chunkId)) {
              wonChunksRef.current.add(chunkId);

              // Update the wonAmount state
              setWonAmount(wonChunksRef.current.size);

              // Update the DOM
              const chunkContainers = scrollContent.querySelectorAll('.chunk-container');
              chunkContainers.forEach(container => {
                if (parseInt(container.dataset.chunkId, 10) === chunkId) {
                  container.classList.add('won-chunk');
                }
              });
            }
          }
        });
      }

      // Check if scrollOffset exceeds squareSize and set currentIndexShown to null only once
      if (scrollOffset > squareSize && !hasSetNull) {
        currentIndexRef.current = null; // Initialize the ref
        setCurrentIndexShown(null);
        hasSetNull = true; // Set the flag to true to prevent further execution
      }

      // Apply transform for smooth scrolling
      scrollContent.style.transform = `translateX(-${scrollOffset}px)`;

      // Continue animation
      animationRef.current = requestAnimationFrame(animate);
    };

    // Store animation reference
    animationRef.current = requestAnimationFrame(animate);

    // Clean up on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [allItems, totalItems, isGameActive, getItemFullWidth, getItemsWithPositions, updateChunkClassList,
      checkChunkCompletion, squareSize, buffer, validSpacePosition, setWonAmount]);

  // Calculate the valid space position in pixels
  const calculateCenteredPosition = () => {
    const screenWidth = window.innerWidth;
    const halfScreenWidth = screenWidth * 0.5; // 50% of screen width
    const validSpaceWidth = squareSize * 1.0; // Width of the valid space
    return halfScreenWidth - (validSpaceWidth / 2); // Center position
  };

  // Initial calculation
  const [centeredValidSpacePosition, setCenteredValidSpacePosition] = useState(calculateCenteredPosition());

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      setCenteredValidSpacePosition(calculateCenteredPosition());
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [squareSize]); // Recalculate if squareSize changes

  // Add effect to update startFrom when window size changes
  useEffect(() => {
    const handleResize = () => {
      const aspectRatio = window.innerWidth / window.innerHeight;
      setStartFrom(aspectRatio >= 1 ? 32 : 16);
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array means this runs once on mount

  // Add this function to remove any failed-attempt classes from all chunks
  const clearAllFailedAttemptClasses = () => {
    if (!scrollContentRef.current) return;

    const chunkContainers = scrollContentRef.current.querySelectorAll('.chunk-container');
    chunkContainers.forEach(container => {
      container.classList.remove('failed-attempt');
    });

    // Also clear the failedChunksRef set
    failedChunksRef.current.clear();
  };


  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const speeds = isTouchDevice ?  [
    3,
    3.5,
    4,
    4.7,
    5.7
  ] : [
    3.5,
    4,
    4.8,
    5.5,
    6.2
  ] 

  // Update the effect that controls speed progression
  useEffect(() => {


    // Only set up timers if the game is active
    if (!isGameActive) {
      // Reset speed ref when game is not active
      scrollSpeedRef.current = speeds[0];
      setScrollSpeed(speeds[0]); // Also update state for consistency
      return;
    }
    
    console.log("Game started - setting up speed progression timers");
    
    // Set timers for speed changes using the ref instead of state
    const timer1 = setTimeout(() => {
      console.log("Increasing speed to ", speeds[1]);
      scrollSpeedRef.current = speeds[1]; // Update ref instead of state
      setScrollSpeed(speeds[1]); // Update state for debugging/display only
    }, 8000); // 12 seconds
    
    const timer2 = setTimeout(() => {
      console.log("Increasing speed to ", speeds[2]);
      scrollSpeedRef.current = speeds[2]; // Update ref instead of state
      setScrollSpeed(speeds[2]); // Update state for debugging/display only
    }, 16000); // 24 seconds
    
    const timer3 = setTimeout(() => {
      console.log("Increasing speed to ", speeds[3]);
      scrollSpeedRef.current = speeds[3]; // Update ref instead of state
      setScrollSpeed(speeds[3]); // Update state for debugging/display only
    }, 24000); // 36 seconds
    

    const timer4 = setTimeout(() => {   
      console.log("Increasing speed to ", speeds[4]);
      scrollSpeedRef.current = speeds[4]; // Update ref instead of state
      setScrollSpeed(speeds[4]); // Update state for debugging/display only
    }, 32000); // 36 seconds
    
    // Clean up timers when the game ends or the component unmounts
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [isGameActive]); // Only re-run this effect when game state changes

  // Add a new state for the faster message
  const [showFasterMessage, setShowFasterMessage] = useState(false);
  const lastSpeedRef = useRef(speeds[0]); // Track the last speed to detect changes

  // Add this effect to show "Faster!" message when speed changes
  useEffect(() => {
    // Only show the message if the speed actually increases
    if (scrollSpeed > lastSpeedRef.current) {
      // Show the message
      setShowFasterMessage(true);
      
      // Hide the message after 1.5 seconds
      const timer = setTimeout(() => {
        setShowFasterMessage(false);
      }, 1500);
      
      // Update the last speed ref
      lastSpeedRef.current = scrollSpeed;
      
      // Clean up
      return () => clearTimeout(timer);
    }
  }, [scrollSpeed]); // Run this effect when scrollSpeed changes

  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    
    // Add the animation keyframes
    style.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
        20% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
        80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
      }
    `;
    
    // Add the style to the document head
    document.head.appendChild(style);
    
    // Clean up
    return () => {
      document.head.removeChild(style);
    };
  }, []); // Only run once

  return (
    <div className="page">

      <div
        ref={outerContainerRef}
        className="outer-container"
      >
        {/* Container for items */}
        <div
          ref={scrollContentRef}
          style={{
            position: 'absolute',
            height: '100%',
            left: 0,
            top: 0,
            willChange: 'transform'
          }}
        />

        {/* Target square - this is where the player needs to hit valid items */}
        <div
          className="valid-space"
          style={{
            width: squareSize * 1.0,
            height: squareSize * 1.0,
            left: `${centeredValidSpacePosition}px`
          }}
        ></div>
      </div>

      {/* Faster message */}
      {showFasterMessage && (
        <div className="faster-message" style={{
          position: 'fixed',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#fff',
          fontWeight: '300',
          fontSize: '14px',
          zIndex: 1000,
          animation: 'fadeInOut 1.5s ease-in-out'
        }}>
          Faster!
        </div>
      )}

    </div>
  );
};

export default Beats;
